import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Users,
  Copy,
  Share2,
  Gift,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  RefreshCw,
  UserPlus,
  CheckCircle2,
  Loader2,
  Send
} from "lucide-react";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";
import { supabase } from '@/lib/supabase';

interface ShareModuleProps {
  challengeId: number;
  challengeName: string;
  referralCode?: string;
  referralLink?: string;
  shareLinks?: {
    whatsapp: string;
    facebook: string;
    twitter: string;
  };
  isOpen?: boolean;
  onClose?: () => void;
  hideBetfredButton?: boolean;
  showOnlyBetfredButton?: boolean;
}

interface ChallengeReferralData {
  referralCode: string;
  referralUrl: string;
  referredCount: number;
}

interface BetfredSignupData {
  hasSignedUp: boolean;
  signupDate?: string;
  verified: boolean;
  entriesAwarded: number;
  verificationStatus?: string;
  verificationAttempts?: number;
  betfredEmail?: string;
  id?: number;
}

// Type for the friend object in the invite form
interface Friend {
  name: string;
  phone: string;
}

export function ShareModule({ challengeId, challengeName, referralCode, referralLink, shareLinks, isOpen, onClose, hideBetfredButton = false, showOnlyBetfredButton = false }: ShareModuleProps) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [internalShowPopup, setInternalShowPopup] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const showSharePopup = isOpen !== undefined ? isOpen : internalShowPopup;
  const setShowSharePopup = onClose !== undefined 
    ? (open: boolean) => { if (!open) onClose(); else if (isOpen === undefined) setInternalShowPopup(open); }
    : setInternalShowPopup;
  const [showBetfredPopup, setShowBetfredPopup] = useState(false);
  const [betfredEmail, setBetfredEmail] = useState("");
  const [friends, setFriends] = useState<Friend[]>([]); // State for invited friends
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({}); // State for field-specific errors

  // Fallback to API query if props not provided (backward compatibility)
  const { data: referralData } = useQuery<ChallengeReferralData>({
    queryKey: ["/api/v1/challenges/referral-code"],
    enabled: isAuthenticated && (!referralCode || !referralLink),
  });

  // Query Betfred signup status for this challenge
  const { data: betfredData } = useQuery<BetfredSignupData>({
    queryKey: ["/api/betfred-challenge-signup", challengeId],
    enabled: isAuthenticated && !!challengeId,
  });

  // Query global Betfred verification status (if user has ever been verified)
  const { data: globalStatus } = useQuery<{ hasBeenVerified: boolean }>({
    queryKey: ["/api/betfred-global-status"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: isAuthenticated,
    staleTime: 0, // Always fresh data
    refetchOnWindowFocus: true,
  });

  const finalReferralLink = referralLink || referralData?.referralUrl || '';
  const finalReferralCode = referralCode || referralData?.referralCode || '';

  // Generate proper referral URL using the server route format
  const shareUrlWithChallenge = finalReferralCode && challengeId
    ? `${window.location.origin}/ref/${challengeId}/${finalReferralCode}/direct`
    : finalReferralLink;

  const referralMessage = shareUrlWithChallenge
    ? `🎉 Join me in the ${challengeName} challenge on PrizeDrop!\n\nUse my referral link to compete with me and climb the leaderboard! 🏆\n\n${shareUrlWithChallenge}`
    : '';

  const handleCopyLink = async () => {
    if (!shareUrlWithChallenge) return;

    try {
      // Copy the full referral message with challenge name and link
      await navigator.clipboard.writeText(referralMessage);
      setCopied(true);
      toast({
        title: "Referral Message Copied!",
        description: `Your ${challengeName} referral message has been copied to clipboard`,
      });
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Please copy the message manually",
        variant: "destructive",
      });
    }
  };

  const handleShare = async (platform: string) => {
    let shareUrl = '';

    switch (platform) {
      case "whatsapp":
        const whatsappReferralUrl = `${window.location.origin}/ref/${challengeId}/${finalReferralCode}/wa`;
        const whatsappMessage = `🎉 Join me in the ${challengeName} challenge on PrizeDrop! Use my referral link to compete with me and climb the leaderboard! 🏆\n\n${whatsappReferralUrl}`;
        shareUrl = shareLinks?.whatsapp || `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`;
        break;
      // Facebook sharing temporarily disabled
      /* case "facebook":
        const facebookReferralUrl = `${window.location.origin}/ref/${challengeId}/${finalReferralCode}/fb`;
        const facebookMessage = `🎉 Join me in the ${challengeName} challenge on PrizeDrop! Use my referral link to compete with me and climb the leaderboard! 🏆\n\n${facebookReferralUrl}`;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        // Copy message to clipboard first
        try {
          await navigator.clipboard.writeText(facebookMessage);
          toast({
            title: "Message Copied!",
            description: "Share this challenge link in Messenger or Facebook!",
            duration: 4000,
          });
        } catch (err) {
          console.error('Failed to copy message:', err);
        }

        if (isMobile) {
          // Mobile: Try Messenger app first
          shareUrl = `fb-messenger://share/?link=${encodeURIComponent(facebookReferralUrl)}&app_id=${process.env.VITE_FACEBOOK_APP_ID || ''}`;
          
          // Fallback chain for mobile
          setTimeout(() => {
            // Try Facebook app
            window.location.href = `fb://profile`;
            setTimeout(() => {
              // Final fallback to mobile web
              window.open(`https://m.facebook.com/sharer.php?u=${encodeURIComponent(facebookReferralUrl)}`, "_blank");
            }, 1000);
          }, 2000);
        } else {
          // Desktop: Open Messenger web
          shareUrl = `https://www.messenger.com/`;
          
          // Fallback to Facebook sharing
          setTimeout(() => {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(facebookReferralUrl)}`, "_blank", "width=600,height=400");
          }, 3000);
        }
        break; */
      case "twitter":
        const twitterReferralUrl = `${window.location.origin}/ref/${challengeId}/${finalReferralCode}/x`;
        const twitterMessage = `🎉 Join me in the ${challengeName} challenge on PrizeDrop! Use my referral link to compete with me and climb the leaderboard! 🏆`;
        shareUrl = shareLinks?.twitter || `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterMessage)}&url=${encodeURIComponent(twitterReferralUrl)}`;
        break;
    }

    if (shareUrl) {
      // Open the sharing window
      window.open(shareUrl, "_blank", "width=600,height=400,noopener,noreferrer");

      // Track the challenge referral share
      try {
        await apiRequest("POST", `/api/v1/challenges/${challengeId}/share`, {
          body: {
            platform,
            shareType: 'referral'
          }
        });

        toast({
          title: "Referral shared successfully!",
          description: `You shared your ${challengeName} referral link on ${platform}!`,
        });
      } catch (error) {
        console.error('Error tracking challenge referral share:', error);
        toast({
          title: "Sharing opened",
          description: `Your ${platform} sharing window has opened. Share to refer friends to the challenge!`,
        });
      }
    }
  };

  // Betfred signup mutation
  const betfredSignupMutation = useMutation({
    mutationFn: async ({ challengeId, betfredEmail }: { challengeId: number; betfredEmail: string }) => {
      const response = await apiRequest('POST', '/api/betfred-signup', {
        body: {
          challengeId,
          betfredEmail,
          awardEntries: true // Special flag to award entries instead of referrals
        }
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/betfred-challenge-signup", challengeId] });
      toast({
        title: "Betfred Email Submitted!",
        description: "We'll verify your Betfred account and award you +2 referral entries once confirmed.",
        duration: 6000,
      });
      setBetfredEmail("");
      setShowBetfredPopup(false);
    },
    onError: (error: any) => {
      toast({
        title: "Signup Failed",
        description: error.message || "Failed to submit Betfred email. Please try again.",
        variant: "destructive",
        duration: 6000,
      });
    },
  });

  // Betfred retry mutation
  const betfredRetryMutation = useMutation({
    mutationFn: async ({ challengeId, betfredEmail }: { challengeId: number; betfredEmail?: string }) => {
      const response = await apiRequest('POST', '/api/betfred-signup/retry', {
        body: {
          challengeId,
          betfredEmail: betfredEmail || undefined,
        }
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/betfred-challenge-signup", challengeId] });
      toast({
        title: "Retry Scheduled!",
        description: "We'll retry verifying your Betfred account. Check back in a minute.",
        duration: 6000,
      });
      setBetfredEmail("");
      setShowBetfredPopup(false);
    },
    onError: (error: any) => {
      toast({
        title: "Retry Failed",
        description: error.message || "Failed to retry verification. Please try again later.",
        variant: "destructive",
        duration: 6000,
      });
    },
  });

  const handleBetfredSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!betfredEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your Betfred email address.",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(betfredEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    try {
      await betfredSignupMutation.mutateAsync({
        challengeId,
        betfredEmail: betfredEmail.trim()
      });
    } catch (error) {
      console.error('Betfred signup error:', error);
    }
  };

  const handleBetfredRetry = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await betfredRetryMutation.mutateAsync({
        challengeId,
        betfredEmail: betfredEmail.trim() || undefined
      });
    } catch (error) {
      console.error('Betfred retry error:', error);
    }
  };

  const handleEarnEntryClick = () => {
    if (!betfredData?.hasSignedUp) {
      // First time users go to signup
      setShowBetfredPopup(true);
    } else if (betfredData.verified) {
      // Already verified, show success message
      toast({
        title: "+2 Referral Entries Already Earned!",
        description: "You've already earned your +2 Betfred referral entries for this challenge.",
        duration: 4000,
      });
    } else {
      // Show status popup for pending/failed verifications
      setShowBetfredPopup(true);
    }
  };

  // State for invite management
  const [friendInvites, setFriendInvites] = useState([
    { id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' },
  ]);
  const [successfulInvitesCount, setSuccessfulInvitesCount] = useState(0);

  // Function to add more friend invite rows
  const addMoreFriendRows = () => {
    const newId = Math.max(...friendInvites.map(f => f.id)) + 1;
    setFriendInvites([...friendInvites, { id: newId, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }]);
  };

  // Function to update a friend invite
  const updateFriendInvite = (id: number, field: string, value: string) => {
    setFriendInvites(prev => 
      prev.map(invite => 
        invite.id === id 
          ? { ...invite, [field]: value, ...(field === 'phone' ? { phoneError: '' } : {}) }
          : invite
      )
    );
  };

  // Function to remove a friend invite row
  const removeFriendInvite = (id: number) => {
    if (friendInvites.length > 1) {
      setFriendInvites(prev => prev.filter(invite => invite.id !== id));
    }
  };

  // South African phone number validation
  const validateSouthAfricanPhone = (phone: string) => {
    if (!phone || phone.trim() === '') return { isValid: false, message: 'Phone number is required' };

    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^(\+27|0)[6-8][0-9]{8}$/;

    if (!phoneRegex.test(cleanPhone)) {
      return { 
        isValid: false, 
        message: 'Please enter a valid SA number (e.g., 0821234567 or +27821234567)' 
      };
    }

    return { isValid: true, message: '' };
  };

  // Function to send individual invite
  const sendIndividualInvite = async (inviteId: number) => {
    const invite = friendInvites.find(inv => inv.id === inviteId);
    if (!invite) return;

    // Validate required fields
    if (!invite.name.trim()) {
      toast({
        title: "Missing Name",
        description: "Please enter your friend's name.",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number
    const phoneValidation = validateSouthAfricanPhone(invite.phone);
    if (!phoneValidation.isValid) {
      setFriendInvites(prev => 
        prev.map(inv => 
          inv.id === inviteId 
            ? { ...inv, phoneError: phoneValidation.message }
            : inv
        )
      );
      toast({
        title: "Invalid Phone Number",
        description: phoneValidation.message,
        variant: "destructive",
      });
      return;
    }

    // Set sending state
    setFriendInvites(prev => 
      prev.map(inv => 
        inv.id === inviteId 
          ? { ...inv, sending: true, phoneError: '' }
          : inv
      )
    );

    try {
      // Get the auth token properly
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({
          title: "Authentication Error",
          description: "Please log in to send invitations",
          variant: "destructive",
        });
        setFriendInvites(prev => prev.map((f, i) => 
          f.id === inviteId ? { ...f, sending: false } : f
        ));
        return;
      }

      const response = await fetch('/api/friends/invite-prizedrop', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          friendName: invite.name.trim(),
          friendEmail: invite.email.trim() || undefined,
          friendPhone: invite.phone.trim(),
          challengeId: challengeId // Send challenge-specific referral link
        })
      });

      let responseData;
      const responseText = await response.text();

      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('Invalid JSON response:', responseText);
        throw new Error('Invalid server response');
      }

      if (response.ok) {
        setSuccessfulInvitesCount(prev => prev + 1);

        // Remove the successfully sent invite row to show a clean form
        setFriendInvites(prev => {
          const updated = prev.filter(inv => inv.id !== inviteId);
          // If all invites were removed, add a fresh empty one
          if (updated.length === 0) {
            return [{ id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' }];
          }
          return updated;
        });

        toast({
          title: "Invitation Sent!",
          description: `${invite.name} will receive an SMS invitation to join this challenge`,
        });
      } else {
        toast({
          title: "Error",
          description: responseData?.error || `Server error: ${response.status}`,
          variant: "destructive",
        });
        setFriendInvites(prev => 
          prev.map(inv => 
            inv.id === inviteId 
              ? { ...inv, sending: false }
              : inv
          )
        );
      }
    } catch (error: any) {
      console.error('Friend invitation error:', error);
      toast({
        title: "Network Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive",
      });
      setFriendInvites(prev => 
        prev.map(inv => 
          inv.id === inviteId 
            ? { ...inv, sending: false }
            : inv
        )
      );
    }
  };

  // Function to reset invite form
  const resetInviteForm = () => {
    setFriendInvites([
      { id: 1, name: '', email: '', phone: '', sending: false, sent: false, phoneError: '' },
    ]);
    setSuccessfulInvitesCount(0);
  };



  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Share Referral Link Button */}
      {!showOnlyBetfredButton && (
        <div className="w-full">
          <Button
            onClick={() => setShowSharePopup(true)}
            disabled={!finalReferralLink}
            variant="outline"
            className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold py-6 text-lg"
            data-testid="button-share-referral-link"
          >
            <Share2 className="h-5 w-5 mr-2" />
            Share Referral Link to Socials
          </Button>
        </div>
      )}

      <div className="w-full">
        {/* Only show Betfred button if user hasn't been verified yet (either globally or for this challenge) */}
        {!hideBetfredButton && !betfredData?.verified && !globalStatus?.hasBeenVerified && (
          <Button
            onClick={handleEarnEntryClick}
            disabled={betfredSignupMutation.isPending || betfredRetryMutation.isPending}
            className={`w-full py-4 sm:py-6 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${showOnlyBetfredButton ? '' : 'mt-3'} text-white active:scale-95 ${
              betfredData?.verificationStatus === 'pending'
                ? 'bg-blue-600 hover:bg-blue-700'
                : betfredData?.verificationStatus === 'failed' || betfredData?.verificationStatus === 'invalid'
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            size="lg"
            data-testid="button-earn-another-entry"
          >
            {betfredSignupMutation.isPending || betfredRetryMutation.isPending ? (
              <>
                <Clock className="h-4 w-4 sm:h-5 w-5 mr-2 sm:mr-3 animate-spin" />
                <span className="hidden sm:inline">Processing...</span>
                <span className="sm:hidden">Processing...</span>
              </>
            ) : betfredData?.verificationStatus === 'pending' ? (
              <>
                <Clock className="h-4 w-4 sm:h-5 w-5 mr-2 sm:mr-3 animate-spin" />
                <span className="hidden sm:inline">Verification in Progress</span>
                <span className="sm:hidden">Verifying...</span>
              </>
            ) : betfredData?.verificationStatus === 'failed' || betfredData?.verificationStatus === 'invalid' ? (
              <>
                <RefreshCw className="h-4 w-4 sm:h-5 w-5 mr-2 sm:mr-3" />
                <span className="hidden sm:inline">Retry Verification</span>
                <span className="sm:hidden">Retry</span>
              </>
            ) : (
              <>
                <Gift className="h-4 w-4 sm:h-5 w-5 mr-2 sm:mr-3" />
                <span className="hidden sm:inline">Get +2 referral entries added to your challenge total</span>
                <span className="sm:hidden">Get +2 referral entries</span>
              </>
            )}
          </Button>
        )}
      </div>

      {/* Mobile-Optimized Share Popup Modal */}
      <Dialog open={showSharePopup} onOpenChange={setShowSharePopup}>
        <DialogContent className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:max-w-md lg:max-w-xl xl:max-w-2xl rounded-lg border bg-background p-4 md:p-6 shadow-lg z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
              <Users className="h-5 w-5 text-[#0066cc]" />
              {friends.length > 0 ? "Invite Friends to PrizeDrop" : `Refer Friends to ${challengeName}`}
            </DialogTitle>
            {friends.length > 0 && (
              <DialogDescription className="text-gray-600">
                Invite your friends to join PrizeDrop and help them discover amazing competitions!
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-6">
            {/* Invite Friends Form Section */}
            {friends.length > 0 && (
              <div className="space-y-3 py-4">
                {/* Header row - Hidden on mobile */}
                <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-sm font-medium text-gray-700">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Email (Optional)</div>
                  <div className="col-span-3">Phone Number</div>
                  <div className="col-span-3"></div>
                </div>

                {friendInvites.map((invite, index) => (
                  <div key={invite.id} className="space-y-3 p-3 md:p-4 border border-gray-200 rounded-lg bg-gray-50">
                    {/* Mobile Layout */}
                    <div className="block md:hidden space-y-3">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-600 mb-1">Name *</label>
                        <input
                          type="text"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                          placeholder="John Doe"
                          value={invite.name}
                          onChange={(e) => updateFriendInvite(invite.id, 'name', e.target.value)}
                          data-testid={`input-friend-name-${index}`}
                          disabled={invite.sending || invite.sent}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-600 mb-1">Email (Optional)</label>
                        <input
                          type="email"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                          placeholder="friend@example.com"
                          value={invite.email}
                          onChange={(e) => updateFriendInvite(invite.id, 'email', e.target.value)}
                          data-testid={`input-friend-email-${index}`}
                          disabled={invite.sending || invite.sent}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-gray-600 mb-1">Phone Number *</label>
                        <input
                          type="tel"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${
                            invite.phoneError 
                              ? 'border-red-300 focus:ring-red-500' 
                              : invite.sent 
                                ? 'border-green-300' 
                                : 'border-gray-300 focus:ring-blue-500'
                          }`}
                          placeholder="0821234567"
                          value={invite.phone}
                          onChange={(e) => updateFriendInvite(invite.id, 'phone', e.target.value)}
                          data-testid={`input-friend-phone-${index}`}
                          disabled={invite.sending || invite.sent}
                        />
                        {invite.phoneError && (
                          <p className="text-red-500 text-xs mt-1">{invite.phoneError}</p>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => sendIndividualInvite(invite.id)}
                          disabled={invite.sending || invite.sent}
                          className={`w-full h-12 transition-colors ${
                            invite.sent 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          } text-white`}
                          data-testid={`button-send-invite-${index}`}
                        >
                          {invite.sending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : invite.sent ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Sent!
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4 mr-2" />
                              Send Invite
                            </>
                          )}
                        </Button>
                        {friendInvites.length > 1 && (
                          <Button
                            variant="outline"
                            onClick={() => removeFriendInvite(invite.id)}
                            className="w-full h-10 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={invite.sending}
                            data-testid={`button-remove-friend-${index}`}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Remove
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden md:grid grid-cols-12 gap-2 items-start">
                      <input
                        type="text"
                        className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                        placeholder="John Doe"
                        value={invite.name}
                        onChange={(e) => updateFriendInvite(invite.id, 'name', e.target.value)}
                        data-testid={`input-friend-name-${index}`}
                        disabled={invite.sending || invite.sent}
                      />

                      <input
                        type="email"
                        className={`col-span-3 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${invite.sent ? 'border-green-300' : 'border-gray-300 focus:ring-blue-500'}`}
                        placeholder="friend@example.com"
                        value={invite.email}
                        onChange={(e) => updateFriendInvite(invite.id, 'email', e.target.value)}
                        data-testid={`input-friend-email-${index}`}
                        disabled={invite.sending || invite.sent}
                      />

                      <div className="col-span-3 space-y-1">
                        <input
                          type="tel"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-base ${
                            invite.phoneError 
                              ? 'border-red-300 focus:ring-red-500' 
                              : invite.sent 
                                ? 'border-green-300' 
                                : 'border-gray-300 focus:ring-blue-500'
                          }`}
                          placeholder="27821234567"
                          value={invite.phone}
                          onChange={(e) => updateFriendInvite(invite.id, 'phone', e.target.value)}
                          data-testid={`input-friend-phone-${index}`}
                          disabled={invite.sending || invite.sent}
                        />
                        {invite.phoneError && (
                          <p className="text-red-500 text-xs">{invite.phoneError}</p>
                        )}
                      </div>

                      <div className="col-span-3 flex gap-1">
                        <Button
                          onClick={() => sendIndividualInvite(invite.id)}
                          disabled={invite.sending || invite.sent}
                          className={`flex-1 transition-colors ${
                            invite.sent 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          } text-white`}
                          size="sm"
                          data-testid={`button-send-invite-${index}`}
                        >
                          {invite.sending ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              Sending
                            </>
                          ) : invite.sent ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Sent
                            </>
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4 mr-1" />
                              Send
                            </>
                          )}
                        </Button>
                        {friendInvites.length > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeFriendInvite(invite.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={invite.sending}
                            data-testid={`button-remove-friend-${index}`}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add More button */}
                <Button
                  variant="outline"
                  onClick={addMoreFriendRows}
                  className="w-full mt-4 h-12"
                  data-testid="button-add-more-friends"
                  disabled={friendInvites.some(invite => invite.sending)} // Only disable while sending
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add More Friends
                </Button>
              </div>
            )}

            {/* Benefits Card */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Compete Together!</span>
                <Badge variant="secondary" className="bg-green-100 text-green-700 ml-auto">
                  Leaderboard Race
                </Badge>
              </div>
              <p className="text-xs text-gray-600">
                Challenge your friends and climb the leaderboard together!
              </p>
            </div>

            {/* Copy Link and WhatsApp Side by Side */}
            {!friends.length && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 text-sm sm:text-base">Share Your Referral Link</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <Button
                    onClick={handleCopyLink}
                    disabled={!shareUrlWithChallenge}
                    variant="outline"
                    className="py-3 sm:py-4 h-auto active:scale-95 transition-transform"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    <span className="text-sm sm:text-base">
                      {copied ? "✓ Link Copied!" : "Copy Referral Link"}
                    </span>
                  </Button>
                  <Button
                    onClick={() => handleShare("whatsapp")}
                    disabled={!shareUrlWithChallenge}
                    variant="outline"
                    className="py-3 sm:py-4 h-auto text-green-700 hover:bg-green-50 border-green-200 active:scale-95 transition-all duration-150"
                  >
                    <FaWhatsapp className="h-4 w-4 mr-2" />
                    <span className="text-sm sm:text-base">WhatsApp</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Referral Stats */}
            {!friends.length && referralData?.referredCount > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Users className="h-4 w-4" />
                  <p className="text-sm font-medium">
                    {referralData.referredCount} friend{referralData.referredCount !== 1 ? 's' : ''} referred!
                  </p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {friends.length > 0 && successfulInvitesCount > 0 && (
              <div className="text-center py-3">
                <div className="text-green-600 font-medium">
                  <CheckCircle2 className="h-5 w-5 inline-block mr-1" />
                  {successfulInvitesCount} invite(s) sent successfully!
                </div>
              </div>
            )}

            {/* Button to trigger the invite friends form */}
            {!friends.length && (
              <Button
                onClick={() => setFriends([{ name: "", phone: "" }])}
                variant="outline"
                className="w-full py-3 sm:py-4 h-auto active:scale-95 transition-transform"
              >
                <Users className="h-4 w-4 mr-2" />
                <span className="text-sm sm:text-base">Invite Friends</span>
              </Button>
            )}

            {/* Close Button */}
            {friends.length > 0 && (
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setFriends([]);
                    resetInviteForm();
                    setShowSharePopup(false);
                  }}
                  className="w-full h-12"
                  data-testid="button-close-modal"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Betfred Earn Referrals Popup */}
      <Dialog open={showBetfredPopup} onOpenChange={setShowBetfredPopup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              Get +2 Referral Entries via Betfred
            </DialogTitle>
          </DialogHeader>

          <div>
            <div className="space-y-4">
            {betfredData?.verified ? (
              /* Already verified - show success */
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-green-900 mb-2">
                    +2 Referral Entries Already Earned!
                  </h3>
                  <p className="text-green-700">
                    You've already earned your +2 Betfred referral entries for this challenge.
                  </p>
                </CardContent>
              </Card>
            ) : betfredData?.hasSignedUp ? (
              /* Show verification status */
              <Card className={`border-2 ${
                betfredData.verificationStatus === 'pending' ? 'border-blue-200 bg-blue-50' :
                'border-orange-200 bg-orange-50'
              }`}>
                <CardContent className="p-4 text-center">
                  {betfredData.verificationStatus === 'pending' ? (
                    <>
                      <Clock className="h-12 w-12 text-blue-600 mx-auto mb-3 animate-spin" />
                      <h3 className="text-lg font-bold text-blue-900 mb-2">Verification in Progress</h3>
                      <p className="text-blue-700">
                        We're verifying your Betfred account. You'll get +2 referral entries once confirmed.
                      </p>
                    </>
                  ) : betfredData.verificationStatus === 'invalid' ? (
                    <>
                      <XCircle className="h-12 w-12 text-red-600 mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-red-900 mb-2">Email Not Found</h3>
                      <p className="text-red-700 mb-4">
                        Your email wasn't found in Betfred's system. Please sign up to Betfred first, then try again.
                      </p>

                      <div className="space-y-3">
                        <Button
                          onClick={() => window.open('https://www.betfred.co.za/', '_blank')}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Sign Up to Betfred
                        </Button>

                        <div className="border-t pt-3">
                          <p className="text-sm text-gray-600 mb-2">
                            Or try a different email:
                          </p>
                          <form onSubmit={handleBetfredRetry} className="space-y-2">
                            <Input
                              type="email"
                              placeholder="different.email@example.com"
                              value={betfredEmail}
                              onChange={(e) => setBetfredEmail(e.target.value)}
                              required
                            />
                            <Button
                              type="submit"
                              disabled={betfredRetryMutation.isPending}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                              size="sm"
                            >
                              {betfredRetryMutation.isPending ? (
                                <>
                                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                                  Retrying...
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Try Again
                                </>
                              )}
                            </Button>
                          </form>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-12 w-12 text-orange-600 mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-orange-900 mb-2">Verification Failed</h3>
                      <p className="text-orange-700 mb-4">
                        Technical issue occurred during verification.
                      </p>

                      <div className="space-y-3">
                        <p className="text-sm text-gray-600">
                          Retry verification:
                        </p>
                        <form onSubmit={handleBetfredRetry} className="space-y-2">
                          <Input
                            type="email"
                            placeholder="your.email@example.com"
                            value={betfredEmail}
                            onChange={(e) => setBetfredEmail(e.target.value)}
                          />
                          <Button
                            type="submit"
                            disabled={betfredRetryMutation.isPending}
                            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                            size="sm"
                          >
                            {betfredRetryMutation.isPending ? (
                              <>
                                <Clock className="h-4 w-4 mr-2 animate-spin" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Retry Verification
                              </>
                            )}
                          </Button>
                        </form>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* New signup form */
              <>
                <Card className="border-2 border-green-200 bg-green-50">
                  <CardContent className="p-4 text-center">
                    <Gift className="h-12 w-12 text-green-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-green-900 mb-2">
                      Get +2 Referral Entries!
                    </h3>
                    <p className="text-green-700">
                      Sign up to Betfred and get +2 referral entries for {challengeName}
                    </p>
                  </CardContent>
                </Card>

                <div className="space-y-3">
                  <div className="text-sm text-gray-700">
                    <div className="font-medium mb-1">How it works:</div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Sign up to Betfred (if you haven't already)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Enter your Betfred email below</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>Get +2 referral entries added to your challenge total</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => window.open('https://betfredsa.click/o/3F2EJM', '_blank')}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Sign Up to Betfred First
                  </Button>

                  <div className="border-t pt-3">
                    <form onSubmit={handleBetfredSignup} className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Your Betfred Email Address
                        </label>
                        <Input
                          type="email"
                          placeholder="your.email@example.com"
                          value={betfredEmail}
                          onChange={(e) => setBetfredEmail(e.target.value)}
                          required
                          data-testid="input-betfred-email"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Use the same email you registered with Betfred
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={betfredSignupMutation.isPending}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-2"
                        data-testid="button-submit-betfred"
                      >
                        {betfredSignupMutation.isPending ? (
                          <>
                            <Clock className="h-4 w-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Gift className="h-4 w-4 mr-2" />
                            Get My +2 Referral Entries
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              </>
            )}
          </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}